import { Component, type ErrorInfo, type ReactNode } from 'react';
import { App as AntApp, ConfigProvider } from 'antd';
import trTR from 'antd/locale/tr_TR';
import enUS from 'antd/locale/en_US';
import { RouterProvider } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ThemeProvider, useTheme } from '@/context/ThemeContext';
import { ApplicationSettingsProvider } from '@/context/ApplicationSettingsContext';
import { ErrorScreen } from '@/components/ErrorScreen';
import {
  isChunkLoadError,
  reloadForChunkError,
  shouldReloadForChunkError,
} from '@/lib/chunkLoad';
import { getTheme } from '@/lib/theme';
import router from '@/routes/router';

// Router DIŞINDAKİ hatalar için son güvenlik ağı. Rota içindeki hatalar (lazy
// sayfa chunk'ları dahil) buraya ULAŞMAZ — React Router onları kendi içinde
// yakalar; bu yüzden her rotada errorElement (RouteError) tanımlıdır.
class AppErrorBoundary extends Component<
  { children: ReactNode },
  { error: unknown }
> {
  state = { error: null as unknown };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
    if (shouldReloadForChunkError(error)) reloadForChunkError();
    else console.error(error, errorInfo);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (shouldReloadForChunkError(error)) return null;
    return <ErrorScreen chunkError={isChunkLoadError(error)} />;
  }
}

// Tema + dil bilinçli antd ConfigProvider. Mod değişince tüm site açık/koyu
// arasında geçer; antd locale i18n diline göre seçilir (form/uyarı metinleri).
function ThemedApp() {
  const { mode } = useTheme();
  const { i18n } = useTranslation();
  const locale = i18n.language === 'en' ? enUS : trTR;
  return (
    <ConfigProvider theme={getTheme(mode)} locale={locale}>
      <AntApp>
        {/* Başvuru açık/kapalı durumu tek sefer çekilir, tüm sayfalara yayılır. */}
        <ApplicationSettingsProvider>
          <AppErrorBoundary>
            <RouterProvider router={router} />
          </AppErrorBoundary>
        </ApplicationSettingsProvider>
      </AntApp>
    </ConfigProvider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <ThemedApp />
    </ThemeProvider>
  );
}

export default App;
